import { datadogRum } from "@datadog/browser-rum";
import { isDefined } from "@clipboard-health/util-ts";
import { sanitizeApiPathTemplate } from "@src/appV2/utils/sanitizeApiPathTemplate";
import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";

import { APP_V2_APP_EVENTS } from "../lib/analytics";

const GET_TRANSPORT_TIMEOUT_MS = 10_000;
const NON_GET_TRANSPORT_TIMEOUT_MS = 30_000;
const MAX_TELEMETRY_PATH_SEGMENTS = 10;
const TRANSPORT_TIMEOUT_ERROR_CODES = new Set([AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT]);
const ALLOWLISTED_ERROR_CODES = new Set([
  ...TRANSPORT_TIMEOUT_ERROR_CODES,
  AxiosError.ERR_BAD_REQUEST,
  AxiosError.ERR_BAD_RESPONSE,
  AxiosError.ERR_CANCELED,
  AxiosError.ERR_NETWORK,
]);
const UNKNOWN_PATH = "/unknown";

type TransportRecoveryOutcome = "exhausted" | "recovered" | "replay_started";
type RetryValue<TError> = boolean | number | RetryFunction<TError>;
type RetryFunction<TError> = (failureCount: number, error: TError) => boolean;
const OUTCOME_TELEMETRY = {
  exhausted: { attempt: 2, exhausted: true },
  recovered: { attempt: 2, exhausted: false },
  replay_started: { attempt: 1, exhausted: false },
} as const satisfies Record<TransportRecoveryOutcome, { attempt: 1 | 2; exhausted: boolean }>;

interface TransportRecoveryTelemetry {
  attempt: 1 | 2;
  elapsedTimeMs: number;
  errorCode: string;
  exhausted: boolean;
  method: "GET";
  outcome: TransportRecoveryOutcome;
  path: string;
  timeoutMs: number;
}

export function applyApiTransportPolicy(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  const isGet = isGetRequest({ config });
  const defaultTimeoutMs = isGet ? GET_TRANSPORT_TIMEOUT_MS : NON_GET_TRANSPORT_TIMEOUT_MS;
  const configuredTimeoutMs = config.timeout;
  const callerTimeoutMs =
    isDefined(configuredTimeoutMs) &&
    Number.isFinite(configuredTimeoutMs) &&
    configuredTimeoutMs > 0
      ? configuredTimeoutMs
      : defaultTimeoutMs;

  config.timeout = callerTimeoutMs;
  if (isGet) {
    config.transportStartedAtMs = performance.now();
  }

  return config;
}

export async function replayTimedOutGet(params: {
  error: AxiosError;
  replayRequest: (config: InternalAxiosRequestConfig) => Promise<AxiosResponse<unknown>>;
}): Promise<AxiosResponse<unknown> | undefined> {
  const { error, replayRequest } = params;
  const { config } = error;

  if (!isDefined(config) || !shouldReplayTimedOutGet({ error, config })) {
    return undefined;
  }

  config.transportRetried = true;
  recordTransportRecovery({
    config,
    error,
    outcome: "replay_started",
  });

  // Normal session-authenticated requests must acquire the current token from
  // the request interceptor. Caller-supplied Authorization headers are retained.
  if (config.callerProvidedAuthorization !== true) {
    config.headers.delete("Authorization");
  }

  try {
    const response = await replayRequest(config);
    recordTransportRecovery({
      config: response.config,
      error,
      outcome: "recovered",
    });
    return response;
  } catch (replayError: unknown) {
    const axiosReplayError = axios.isAxiosError(replayError) ? replayError : undefined;

    if (isDefined(axiosReplayError)) {
      axiosReplayError.transportRecoveryExhausted = true;
    }

    recordTransportRecovery({
      config: axiosReplayError?.config ?? config,
      error: axiosReplayError,
      outcome: "exhausted",
    });

    throw replayError;
  }
}

export function markUncertainNonGetTransportTimeout(params: { error: AxiosError }): void {
  const { error } = params;

  if (
    isNamedTransportTimeout({ error }) &&
    isDefined(error.config) &&
    !isGetRequest({ config: error.config }) &&
    !callerAbortedRequest({ error })
  ) {
    error.name = "UncertainTransportOutcomeError";
    error.outcomeUncertain = true;
  }
}

export function isTransportRetryTerminalError(params: { error: unknown }): boolean {
  const { error } = params;

  return (
    axios.isAxiosError(error) &&
    (error.transportRecoveryExhausted === true || error.outcomeUncertain === true)
  );
}

export function getTransportAwareRetry<TError>(retry: RetryValue<TError>): RetryFunction<TError> {
  return (failureCount, error) => {
    if (isTransportRetryTerminalError({ error })) {
      return false;
    }

    if (typeof retry === "function") {
      return retry(failureCount, error);
    }

    return typeof retry === "number" ? failureCount < retry : retry;
  };
}

function shouldReplayTimedOutGet(params: {
  config: InternalAxiosRequestConfig;
  error: AxiosError;
}): boolean {
  const { config, error } = params;

  return (
    isGetRequest({ config }) &&
    config.disableGetTransportRecovery !== true &&
    config.transportRetried !== true &&
    !callerAbortedRequest({ error }) &&
    isNamedTransportTimeout({ error })
  );
}

function isNamedTransportTimeout(params: { error: AxiosError }): boolean {
  const { error } = params;

  return !isDefined(error.response) && TRANSPORT_TIMEOUT_ERROR_CODES.has(error.code ?? "");
}

function callerAbortedRequest(params: { error: AxiosError }): boolean {
  const { error } = params;

  return (
    error.code === AxiosError.ERR_CANCELED ||
    axios.isCancel(error) ||
    error.config?.signal?.aborted === true
  );
}

function isGetRequest(params: { config: InternalAxiosRequestConfig }): boolean {
  const { config } = params;

  return (config.method ?? "get").toUpperCase() === "GET";
}

function recordTransportRecovery(params: {
  config: InternalAxiosRequestConfig;
  error?: AxiosError;
  outcome: TransportRecoveryOutcome;
}): void {
  const { config, error, outcome } = params;
  const { attempt, exhausted } = OUTCOME_TELEMETRY[outcome];
  const telemetry: TransportRecoveryTelemetry = {
    attempt,
    elapsedTimeMs: getElapsedTimeMs({ config }),
    errorCode: getAllowlistedErrorCode({ errorCode: error?.code }),
    exhausted,
    method: "GET",
    outcome,
    path: getSanitizedPath({ url: config.url }),
    timeoutMs: config.timeout ?? GET_TRANSPORT_TIMEOUT_MS,
  };

  datadogRum.addAction(APP_V2_APP_EVENTS.API_GET_TRANSPORT_RECOVERY, telemetry);
}

function getElapsedTimeMs(params: { config: InternalAxiosRequestConfig }): number {
  const { config } = params;

  if (!isDefined(config.transportStartedAtMs)) {
    return 0;
  }

  return performance.now() - config.transportStartedAtMs;
}

function getAllowlistedErrorCode(params: { errorCode: string | undefined }): string {
  const { errorCode } = params;

  return isDefined(errorCode) && ALLOWLISTED_ERROR_CODES.has(errorCode) ? errorCode : "UNKNOWN";
}

function getSanitizedPath(params: { url: string | undefined }): string {
  const { url } = params;

  if (!isDefined(url)) {
    return UNKNOWN_PATH;
  }

  try {
    const { pathname } = new URL(url, "https://api.invalid");
    return sanitizeApiPathTemplate({
      maxPathSegments: MAX_TELEMETRY_PATH_SEGMENTS,
      pathname,
    });
  } catch {
    return UNKNOWN_PATH;
  }
}
